import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  alpha,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Skeleton,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { statisticsService } from '../services/statisticsService';

export const ApiKeyStatsChart: React.FC = () => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState<number>(24);

  const { data, isLoading } = useQuery({
    queryKey: ['apiKeyEventRates', timeRange],
    queryFn: () => statisticsService.getApiKeyEventRates(timeRange),
    refetchInterval: 60000,
  });

  const handleTimeRangeChange = (_: React.MouseEvent<HTMLElement>, newValue: number | null) => {
    if (newValue !== null) {
      setTimeRange(newValue);
    }
  };

  const apiKeyChartData = React.useMemo(() => {
    if (!data?.byApiKey) return [];
    return data.byApiKey.map((k) => ({
      name: k.apiKeyName || 'Unknown',
      totalEvents: k.totalEvents,
    }));
  }, [data?.byApiKey]);

  const maxEvents = React.useMemo(() => {
    if (!data?.bySource?.length) return 1;
    return Math.max(...data.bySource.map((s) => s.totalEvents));
  }, [data?.bySource]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Events by API Key & Source
          </Typography>
          {data && (
            <Typography variant="body2" color="text.secondary">
              {data.totalEvents.toLocaleString()} total &bull; {data.byApiKey.length} keys &bull; {data.bySource.length} sources
            </Typography>
          )}
        </Box>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={handleTimeRangeChange}
          size="small"
          sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.25, fontSize: '0.75rem', textTransform: 'none' } }}
        >
          <ToggleButton value={1}>1h</ToggleButton>
          <ToggleButton value={6}>6h</ToggleButton>
          <ToggleButton value={24}>24h</ToggleButton>
          <ToggleButton value={168}>7d</ToggleButton>
          <ToggleButton value={720}>30d</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Skeleton variant="rounded" height={200} />
          <Skeleton variant="rounded" height={120} />
        </Box>
      ) : !data || (data.byApiKey.length === 0 && data.bySource.length === 0) ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
          <Typography variant="body2" color="text.secondary">
            No API key statistics available yet.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {apiKeyChartData.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.08em' }}>
                By API Key
              </Typography>
              <Box sx={{ width: '100%', height: Math.max(150, apiKeyChartData.length * 40 + 40) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={apiKeyChartData} layout="vertical" margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} horizontal={false} />
                    <XAxis type="number" stroke={theme.palette.text.secondary} style={{ fontSize: '0.75rem' }} tickLine={false} />
                    <YAxis type="category" dataKey="name" stroke={theme.palette.text.secondary} style={{ fontSize: '0.75rem' }} tickLine={false} width={120} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        borderRadius: 8,
                        fontSize: '0.8125rem',
                      }}
                    />
                    <Bar
                      dataKey="totalEvents"
                      name="Events"
                      fill={theme.palette.primary.main}
                      radius={[0, 4, 4, 0]}
                      maxBarSize={24}
                      fillOpacity={0.85}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          )}

          {data.bySource.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.08em' }}>
                By Source
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Source</TableCell>
                      <TableCell>Identifier</TableCell>
                      <TableCell align="right">Events</TableCell>
                      <TableCell sx={{ width: '30%' }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.bySource.map((source) => (
                      <TableRow key={`${source.eventSource}-${source.sourceIdentifier}`} sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell>
                          <Chip label={source.eventSource} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {source.sourceIdentifier || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {source.totalEvents.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <LinearProgress
                            variant="determinate"
                            value={(source.totalEvents / maxEvents) * 100}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                backgroundColor: theme.palette.primary.main,
                              },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
